import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { selectBillData, selectLoading, selectFromDate, selectToDate, selectSelectedBranch, selectSelectedCustomer } from '../../store/slices/creditSettlementReportsSlice';
import { exportStyledExcel, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

/** Some legacy rows store branch_id as a bogus "0" on the per-branch table; treat that as unusable. */
const isUsableBranchId = (id) => {
    const s = String(id ?? '').trim();
    return s !== '' && s !== '0';
};

const getBranchIdForRow = (row, selectedBranch) => {
    if (isUsableBranchId(row.branch_id)) return String(row.branch_id).trim();
    if (isUsableBranchId(selectedBranch?.branch_id)) return String(selectedBranch.branch_id).trim();
    const stored = localStorage.getItem('branchid');
    return isUsableBranchId(stored) ? String(stored).trim() : '';
};

/**
 * Unsettled rows carry '0' in stlmnt_date/remarks rather than NULL, and MySQL
 * zero-dates come through as '0000-00-00'. Treat all of those as "no value".
 */
const isBlankValue = (value) => {
    const s = String(value ?? '').trim();
    return s === '' || s === '0' || s === '0000-00-00' || s === '0000-00-00 00:00:00';
};

const CreditSettlementReportsContent = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    const billData = useSelector(selectBillData);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const selectedCustomer = useSelector(selectSelectedCustomer);

    const rolename = localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const canExport = isCEO || isAdmin;

    const [filteredBillData, setFilteredBillData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        setFilteredBillData(billData);
        setGlobalFilter('');
    }, [billData]);

    const formatDate = (dateString) => {
        if (isBlankValue(dateString)) return '';
        return moment(dateString).isValid() ? moment(dateString).format('DD-MM-YYYY') : '';
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const settlementDateOf = (row) => formatDate(row.stlmnt_date) || '-';
    const remarksOf = (row) => (isBlankValue(row.remarks) ? '-' : String(row.remarks).trim());

    const viewBill = (rowData) => {
        if (rowData.bill_id) {
            const branchId = getBranchIdForRow(rowData, selectedBranch);

            if (!branchId) {
                toast.current?.show({ severity: 'warn', summary: 'Branch missing', detail: 'Please select a branch in the filter to view the receipt.', life: 4000 });
                return;
            }
            navigate(`/billreceipt/${rowData.bill_id}/${branchId}`, {
                state: { userId: rowData.userId ?? rowData.user_id }
            });
        }
    };

    const actionBodyTemplate = (rowData) => {
        return <Button icon="pi pi-eye" label="View" className="p-button-rounded p-button-info p-button-sm" onClick={() => viewBill(rowData)} tooltip="View Receipt" tooltipOptions={{ position: 'top' }} />;
    };

    const exportToPDF = () => {
        if (filteredBillData.length === 0) {
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Credit Settlement Report', 14, 15);

        doc.setFontSize(10);
        let yPos = 25;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
            yPos += 5;
        }
        if (selectedBranch) {
            doc.text(`Branch: ${selectedBranch.branch_name}`, 14, yPos);
            yPos += 5;
        }
        if (selectedCustomer) {
            doc.text(`Customer: ${selectedCustomer}`, 14, yPos);
            yPos += 5;
        }

        const tableBody = filteredBillData.map((b) => [
            b.bill_id || '',
            b.invoice_no || '',
            formatDate(b.invoice_date),
            b.cust_name || '',
            formatCurrency(b.total_paid_amount),
            b.final_mop || '',
            settlementDateOf(b),
            remarksOf(b)
        ]);

        if (isCEO) {
            const total = filteredBillData.reduce((acc, curr) => acc + parseFloat(curr.total_paid_amount || 0), 0);
            tableBody.push(['', '', '', '', formatCurrency(total), '', '', '']);
        }

        autoTable(doc, {
            startY: yPos + 5,
            head: [['Bill ID', 'Invoice No', 'Date', 'Customer', 'Total', 'MOP', 'Settlement Date', 'Remarks']],
            body: tableBody,
            footStyles: { fontStyle: 'bold' },
            styles: { fontSize: 8 }
        });

        doc.save(`CreditSettlementReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const exportToExcel = async () => {
        if (filteredBillData.length === 0) {
            return;
        }

        const excelColumnMap = {
            invoice_no: { header: 'Bill No', getValue: (b) => b.invoice_no || '-' },
            invoice_date: { header: 'Bill Date', getValue: (b) => formatDate(b.invoice_date) },
            invoice_time: { header: 'Bill Time', getValue: (b) => b.invoice_time || '-' },
            cust_name: { header: 'Cust Name', getValue: (b) => b.cust_name || '-' },
            total_paid_amount: { header: 'Amt', getValue: (b) => parseFloat(b.total_paid_amount || 0), numeric: true },
            stlmnt_date: { header: 'Settlement Date', getValue: (b) => settlementDateOf(b) },
            remarks: { header: 'Remarks', getValue: (b) => remarksOf(b) }
        };

        const billColumns = [{ header: 'Sl.No', getValue: (b, index) => index + 1 }, ...visibleBillColumns.map((col) => excelColumnMap[col.field]).filter(Boolean)];

        const filterLines = buildReportFilterLines({ fromDate, toDate, selectedBranch });
        if (selectedCustomer) filterLines.push(`Customer: ${selectedCustomer}`);

        await exportStyledExcel({
            fileName: `CreditSettlementReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Credit Settlement',
            title: 'Credit Settlement Report',
            filterLines,
            columns: billColumns,
            rows: filteredBillData,
            showFooterTotal: isCEO
        });
    };

    const grandTotal = filteredBillData.reduce((sum, item) => sum + parseFloat(item.total_paid_amount || 0), 0);

    useEffect(() => {
        const searchTerm = globalFilter.trim().toLowerCase();
        if (!searchTerm) {
            setFilteredBillData(billData);
            return;
        }
        const filtered = billData.filter(
            (item) =>
                (item.bill_id && item.bill_id.toString().toLowerCase().includes(searchTerm)) ||
                (item.invoice_no && item.invoice_no.toLowerCase().includes(searchTerm)) ||
                (item.cust_name && item.cust_name.toLowerCase().includes(searchTerm)) ||
                (item.final_mop && item.final_mop.toLowerCase().includes(searchTerm)) ||
                (!isBlankValue(item.remarks) && String(item.remarks).toLowerCase().includes(searchTerm))
        );
        setFilteredBillData(filtered);
    }, [globalFilter, billData]);

    // bill_id restarts per branch, so it is not unique once "All Branches" is
    // selected. Pair it with the branch to keep row keys distinct.
    const tableRows = useMemo(
        () =>
            filteredBillData.map((row) => ({
                ...row,
                __rowKey: `${String(row.bill_id)}__${String(getBranchIdForRow(row, selectedBranch))}`
            })),
        [filteredBillData, selectedBranch]
    );

    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const n = parseFloat(String(value).trim());
        return Number.isFinite(n) && n === 0;
    };

    const billColumns = [
        { field: 'invoice_no', header: 'Bill No', sortable: true, style: { minWidth: '140px' } },
        { field: 'invoice_date', header: 'Bill Date', sortable: true, style: { minWidth: '100px' }, body: (row) => formatDate(row.invoice_date) },
        { field: 'invoice_time', header: 'Bill Time', style: { minWidth: '90px' } },
        { field: 'cust_name', header: 'Cust Name', sortable: true, style: { minWidth: '140px' }, body: (row) => row.cust_name || '-' },
        { field: 'total_paid_amount', header: 'Amt', sortable: true, style: { textAlign: 'right', minWidth: '100px' }, body: (row) => `₹${formatCurrency(row.total_paid_amount)}`, hideIfAllZero: true },
        { field: 'stlmnt_date', header: 'Settlement Date', sortable: true, style: { minWidth: '130px' }, body: settlementDateOf },
        { field: 'remarks', header: 'Remarks', sortable: true, style: { minWidth: '180px' }, body: remarksOf }
    ];

    const visibleBillColumns = billColumns.filter((col) => {
        if (!col.hideIfAllZero) return true;
        return !filteredBillData.every((row) => isZeroLike(row?.[col.field]));
    });

    const totalColumnIndex = visibleBillColumns.findIndex((col) => col.field === 'total_paid_amount');
    const billFooterGroup =
        isCEO && totalColumnIndex !== -1 ? (
            <ColumnGroup>
                <Row>
                    <Column footer="" />
                    {visibleBillColumns.map((col, index) => (
                        <Column
                            key={col.field}
                            footer={index === Math.max(0, totalColumnIndex - 1) ? 'Grand Total:' : index === totalColumnIndex ? `₹${formatCurrency(grandTotal)}` : ''}
                            style={index >= totalColumnIndex - 1 && index <= totalColumnIndex ? { textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' } : undefined}
                        />
                    ))}
                    <Column footer="" />
                </Row>
            </ColumnGroup>
        ) : null;

    return (
        <div className="bill-reports-container">
            <Toast ref={toast} />

            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="flex align-items-center gap-2">
                        <h3 className="text-lg font-semibold m-0">Report Data</h3>
                        {filteredBillData.length > 0 && <Tag value={`${filteredBillData.length} Records`} severity="info" className="ml-2" />}
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search bills..." className="w-full" />
                        </span>

                        {canExport && (
                            <>
                                <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportToExcel} disabled={filteredBillData.length === 0} />
                                <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportToPDF} disabled={filteredBillData.length === 0} />
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : filteredBillData.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-6">
                        <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                        <p className="text-xl text-color-secondary">No data available</p>
                        <p className="text-color-secondary mt-2">{billData.length === 0 ? 'Please apply filters to view settled credit bills' : 'No records match your search criteria'}</p>
                    </div>
                ) : (
                    <DataTable
                        value={tableRows}
                        dataKey="__rowKey"
                        showGridlines
                        paginator
                        rows={25}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        tableStyle={{ minWidth: '50rem' }}
                        className="p-datatable-sm"
                        emptyMessage="No settled credit bills found"
                        footerColumnGroup={billFooterGroup}
                    >
                        <Column header="Sl.No" body={(row, options) => options.rowIndex + 1} style={{ minWidth: '60px' }} />
                        {visibleBillColumns.map((col) => (
                            <Column key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable} style={col.style} />
                        ))}
                        <Column header="Actions" body={actionBodyTemplate} style={{ textAlign: 'center', minWidth: '80px' }} />
                    </DataTable>
                )}
            </Card>
        </div>
    );
};

export default CreditSettlementReportsContent;
